import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const BangladeshPage = () => {
  const bangladeshData = {
    name: 'Bangladesh',
    region: 'South Asia',
    color: '#7090A0',
    metaTitle: 'Pharma & Nutraceutical Registration in Bangladesh | DGDA | CTD Dossiers',
    metaDescription: 'Discover how to register pharmaceutical and nutraceutical products in Bangladesh. DGDA requirements, CTD dossier format, supplement and nutraceutical pathways, documents required and market opportunity in a rapidly expanding South Asian market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Bangladesh',
      subtitle: 'A fast-growing, import-supportive South Asian market under the DGDA regime, with CTD-based registration, strong growth in generics & nutraceuticals, and deep ties with Indian manufacturing.',
      cta: 'Start Your Bangladesh Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~169M' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure per Capita', value: '~USD 56' },
      { label: 'Health Expenditure (% of GDP)', value: '~2.5-3%' },
      { label: 'Pharma Import Dependency', value: 'High (India top supplier)' },
      { label: 'Regulatory Authority', value: 'DGDA' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Rapid Growth + Import + Generics',
      stats: [
        'Branded generics and formulations market growing strongly',
        'Indian exports contribute significantly to market',
        'DGDA oversees registration, GMP compliance, import licenses, pricing',
        'Market dominated by local manufacturers for generics',
        'Substantial finished imports for specialty, branded generics, nutraceuticals',
        'Demand for vitamins, minerals, immunity boosters rising in urban areas',
        'Price sensitivity strong but volumes compensate',
        'Indian manufacturers with documentation can access distribution networks'
      ],
      opportunities: [
        'Among South Asia\'s largest populations (169M)',
        'Growing middle-class segments',
        'Pharmaceutical sector expanding rapidly',
        'Strong momentum in imports, generics and nutraceuticals',
        'Urban retail growth supports wellness segment expansion'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: High Treatment Needs + Growth Potential',
      subtitle: 'Bangladesh is experiencing rising NCD burden with continued infectious disease and nutritional challenges.',
      categories: [
        {
          name: 'Non-Communicable Diseases (NCDs) — Rising Burden',
          items: [
            'Hypertension, diabetes, cardiovascular disease becoming major challenges',
            'Chronic respiratory disease prevalence increasing',
            'Urban lifestyles and population growth driving demand',
            'Improving healthcare access creates market expansion',
            'DGDA focus on CTD adoption for quality improvement',
            'Pharma: Antihypertensives, antidiabetics, lipid-lowering, chronic respiratory care, cardio-renal drugs',
            'Nutra: Metabolic health, heart health, immunity, vitamins/minerals, maternal & child nutrients'
          ]
        },
        {
          name: 'Infectious Diseases & Nutrition',
          items: [
            'Infectious diseases remain relevant, especially rural/underserved areas',
            'Maternal/child health continues as priority',
            'Micronutrient deficiencies still present',
            'Essential generics remain stable base',
            'Nutra: Iron/folate, paediatric multivitamins, general wellness supplements',
            'Large urban base and rising wellness awareness support segments'
          ]
        },
        {
          name: 'Best-Fit Product Categories',
          items: [
            'Pharma: Essential generics (antibiotics, antihypertensives, antidiabetics), paediatric formulations, chronic therapies',
            'Nutra: Vitamins/minerals, immunity support, metabolic wellness, women\'s & child health'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Directorate General of Drug Administration (DGDA)',
      description: 'DGDA is the national regulatory body under Ministry of Health & Family Welfare. Bangladesh adopted CTD format for medicine registration. Drug Administration Bangladesh CTD Module 2017 guideline outlines Modules 1-3, quality summary and quality modules.',
      pharma: {
        timeline: 'Varies based on dossier quality and DGDA evaluation process',
        requirements: [
          'CTD format adopted per DGDA 2017 guideline (Modules 1-3)',
          'Bangladesh-registered company must act as applicant',
          'Parts include administrative data, product info, quality/CMC data',
          'Later modules (non-clinical/clinical) may be required for certain products',
          'GMP certification mandatory for manufacturing site',
          'Plant inspection not always compulsory but GMP compliance crucial',
          'DGDA evaluates dossier for quality, safety, efficacy',
          'Registration certificate issued with specified validity',
          'Renewal and variations subject to DGDA rules',
          'Packaging/labelling must meet Bangladesh requirements (language, local agent address)'
        ],
        insight: 'Bangladesh has adopted CTD format via DGDA guidelines. Local agent required. GMP compliance mandatory though plant inspections not always required. Aligning dossiers benefits future expansions.'
      },
      nutra: {
        timeline: 'Depends on product classification and documentation completeness',
        requirements: [
          'Supplements comply with local food-supplement regulations',
          'May require notification or registration depending on ingredients',
          'Full product composition required',
          'GMP for manufacturing site mandatory',
          'Clear claims (not therapeutic/treatment claims)',
          'Package-labelling compliance (Bangla + English as required)',
          'Local agent/importer manages registration and clearance',
          'Urban retail growth supports wellness segment'
        ],
        insight: 'While dedicated nutraceutical regulation less formalized than ASEAN markets, supplements subject to DGDA-linked regulation. Ensure documented supplements, local agent, label compliance and local approval.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration in Bangladesh (CTD Format)',
      steps: [
        'Local Agent / Applicant Appointment - Bangladesh-registered company acts as applicant',
        'Dossier Preparation CTD Format - Per DGDA 2017 guideline (Modules 1-3)',
        'Administrative data, product info, quality/CMC data inclusion',
        'Non-clinical/clinical modules for certain products as required',
        'GMP Declaration / Certificate - Manufacturing site GMP certification',
        'Submission & Review - DGDA evaluates quality, safety, efficacy',
        'Query management and response',
        'Approval & Registration Certificate - Valid for specified duration',
        'Renewal and variations per DGDA rules',
        'Packaging/labelling compliance verification'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceutical & Dietary Supplement Registration',
      steps: [
        'Classification under food-supplement regulations',
        'Notification or registration requirement determination (ingredient-dependent)',
        'Full product composition preparation (quantitative)',
        'GMP certification for manufacturing site',
        'Claims review - ensure not therapeutic/treatment claims',
        'Package-labelling preparation (Bangla + English as required)',
        'Local agent/importer appointment',
        'Registration and clearance documentation management',
        'Compliance verification',
        'Market entry through urban retail channels'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (CTD)': [
        'Application form per DGDA requirements',
        'CTD formatted dossier (Modules 1-3 at minimum)',
        'GMP certificate of manufacturing site',
        'CoA, specifications, stability summary',
        'Pack insert, label (with local agent name & address)',
        'Fee payment receipt',
        'Local agent agreement',
        'Non-clinical/clinical summaries (if applicable)'
      ],
      'Nutraceuticals / Supplements': [
        'Full formula (quantitative) with inactive/active ingredients',
        'GMP certificate for manufacturing site',
        'COA & microbiological certificate for finished product',
        'Label artwork compliant with Bangladesh requirements (language, content)',
        'Importer documentation',
        'CFS/Free Sale if required'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Bangladesh Should Be Part of Your Strategy',
      reasons: [
        'Large population (~169 million) with increasing healthcare access',
        'Strong potential volume market',
        'Growing generics and supplement markets',
        'Indian manufacturers already have significant share',
        'CTD format in place via DGDA guidelines',
        'Price-sensitive but high-volume market',
        'Suitable for Indian generics and well-priced nutraceuticals',
        'Strong manufacturing and supplier ecosystem in India for exports/logistics'
      ],
      bestPortfolio: [
        'Pharma: Essential generics (antibiotics, antihypertensives, antidiabetics), paediatric formulations, chronic therapies',
        'Nutra: Vitamins/minerals, immunity support, metabolic wellness, women\'s & child health'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Bangladesh',
      services: [
        'Market Fit Analysis - Map products vs Bangladesh disease profile and import structure',
        'CTD Dossier Preparation - Align with DGDA CTD guidelines, ensure GMP and quality compliance',
        'Supplement Strategy - Position nutraceuticals with compliant formulations, claims & labelling',
        'Local Agent Coordination - Support selection and documentation for Bangladesh-registered importer/MAH',
        'Query & Lifecycle Support - Assist with DGDA queries, renewals, variations & market-adaptation',
        'Packaging Readiness - Ensure Bangladesh-specific labelling requirements met'
      ]
    },
    
    faqs: [
      {
        question: 'Which authority regulates medicines in Bangladesh?',
        answer: 'The Directorate General of Drug Administration (DGDA) under the Ministry of Health & Family Welfare.'
      },
      {
        question: 'Does Bangladesh use CTD format for registration?',
        answer: 'Yes. DGDA has adopted CTD format (Modules 1-3) for medicines registration, with guidelines published in 2017.'
      },
      {
        question: 'Do I need a local agent for registration?',
        answer: 'Yes. A local Bangladesh-registered company must act as applicant or marketing authorisation holder.'
      },
      {
        question: 'Are Indian GMP certificates accepted?',
        answer: 'Manufacturers must have GMP. While plant inspections are not always required, GMP compliance is mandatory.'
      },
      {
        question: 'How are nutraceuticals regulated?',
        answer: 'Supplements fall under food/dietary product regulations. Full registration/notification varies by product. Ensure correct classification, label/claim compliance and local agent.'
      }
    ]
  };

  return <CountryTemplate countryData={bangladeshData} />;
};
